import { teacherProfileAtom } from '@openeducation/oe-rn-jotai-atoms';
import { useEnvironmentContext } from '@openeducation/oe-rn-providers';
import type { AxiosError } from 'axios';
import { useAtomValue } from 'jotai';
import { useQuery, useMutation, useQueryClient } from 'react-query';

import type { AskTeacherQuestion } from '../interfaces/ask-teacher-question';
import { QueryKeys } from '../interfaces/query-keys';
import { AskTeacherService } from '../services/ask-teacher.service';

interface AnswerQuestionParams {
  answer: string;
}

export function useAskQuestion() {
  const queryClient = useQueryClient();
  const { environment } = useEnvironmentContext();
  const teacherProfile = useAtomValue(teacherProfileAtom);

  const { data: questionsCount } = useQuery<number, AxiosError>(
    [QueryKeys.AskTeacherCount],
    () => AskTeacherService.getQuestionsCount(environment, teacherProfile?.id)
  );

  const {
    data: question,
    isLoading: isLoadingQuestion,
    error: errorQuestion,
    refetch: getNextQuestion,
    remove: removeQuestion,
  } = useQuery<AskTeacherQuestion, AxiosError>(
    [QueryKeys.AskTeacherQuestion],
    () => AskTeacherService.getNextQuestion(environment, teacherProfile?.id),
    { enabled: false }
  );

  const {
    error: errorAnswer,
    isLoading: isLoadingAnswer,
    mutate: answerQuestion,
  } = useMutation(
    ({ answer }: AnswerQuestionParams) => {
      return AskTeacherService.answerQuestion(
        environment,
        answer,
        teacherProfile?.id,
        question?.id?.toString()
      );
    },
    {
      onSuccess: () => {
        removeQuestion();
        queryClient.invalidateQueries(QueryKeys.AskTeacherCount);
      },
    }
  );

  return {
    getNextQuestion,
    answerQuestion,
    questionsCount,
    isLoadingAnswer,
    errorAnswer,
    question,
    errorQuestion,
    isLoadingQuestion,
  };
}
